import React from 'react';
import logoVision from '../../assets/logo.png';

/**
 * Componente que replica fielmente el talonario físico "DETALLE DE COMPRA / RECIBO" de Vision Click.
 * 
 * Props:
 *  - datos: Objeto con todos los campos del recibo
 *  - editable: Boolean (si los campos y checkboxes pueden ser interactivos o son solo lectura)
 *  - onChange: Función (campo, valor) para actualizar datos cuando es interactivo
 */
const ReciboVentaDocumento = ({ datos = {}, editable = false, onChange = () => {} }) => {
    const d = {
        numero: datos.numero || '',
        nombre: datos.cliente_nombre || datos.nombre || '',
        fecha: datos.fecha_emision || datos.fecha || new Date().toISOString().slice(0, 10),
        telefono: datos.cliente_telefono || datos.telefono || '',
        armazon: datos.armazon || '',
        
        // Refracción OD
        esfera_od: datos.esfera_od || '',
        cilindro_od: datos.cilindro_od || '',
        eje_od: datos.eje_od || '',
        adicion_od: datos.adicion_od || '',
        dp_od: datos.dp_od || '',

        // Refracción OI
        esfera_oi: datos.esfera_oi || '',
        cilindro_oi: datos.cilindro_oi || '',
        eje_oi: datos.eje_oi || '',
        adicion_oi: datos.adicion_oi || '',
        dp_oi: datos.dp_oi || '',

        // Checkboxes
        tipo_lente: datos.tipo_lente || [],
        materiales: datos.materiales || [],
        caracteristicas: datos.caracteristicas || [],

        observaciones: datos.observaciones || '',

        recibi_de: datos.recibi_de || datos.cliente_nombre || datos.nombre || '',
        la_cantidad_de: datos.la_cantidad_de || '',

        total: Number(datos.total) || 0,
        abono: Number(datos.abono) || 0,
        saldo: Number(datos.saldo !== undefined ? datos.saldo : (Number(datos.total || 0) - Number(datos.abono || 0))) || 0,
    };

    const toggleLista = (campo, item) => {
        if (!editable) return;
        const actual = Array.isArray(d[campo]) ? [...d[campo]] : [];
        const index = actual.indexOf(item);
        if (index > -1) {
            actual.splice(index, 1);
        } else {
            actual.push(item);
        }
        onChange(campo, actual);
    };

    const tiene = (campo, item) => (d[campo] || []).includes(item);

    const TIPOS_LENTE = [
        'MONOFOCAL',
        'PROGRESIVO',
        'B. FLAT TOP',
        'B. INVISIBLE',
    ];

    const MATERIALES = [
        'CR-39 1.49',
        'ÍNDICE 1.56',
        'ÍNDICE 1.61 / 1.60',
        'ÍNDICE 1.67',
        'ÍNDICE 1.74',
        'CRISTAL 1.52',
        'CRISTAL 1.70',
        'TRIVEX 1.53',
        'POLICARBONATO 1.59',
    ];

    const CARACTERISTICAS = [
        'AR VERDE',
        'AR AZUL',
        'FILTRO AZUL',
        'FOTOCROMÁTICO',
        'ANTI UV',
        'TINTURADO',
        'POLARIZADO',
        'ANTIRAYAS',
        'TRANSITION',
    ];

    return (
        <div className="recibo-talonario-container">
            <style>{`
                .recibo-talonario-container {
                    background: #ffffff;
                    color: #164e9e;
                    font-family: 'Segoe UI', Arial, sans-serif;
                    width: 100%;
                    max-width: 680px;
                    margin: 0 auto;
                    border: 2.5px solid #164e9e;
                    border-radius: 8px;
                    padding: 16px 20px;
                    box-sizing: border-box;
                    box-shadow: 0 4px 20px rgba(22, 78, 158, 0.08);
                    position: relative;
                }

                .rc-encabezado {
                    display: grid;
                    grid-template-columns: 200px 1fr;
                    gap: 16px;
                    align-items: center;
                    border-bottom: 2px solid #164e9e;
                    padding-bottom: 12px;
                }

                .rc-logo-block {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    border-right: 2px solid #164e9e;
                    padding-right: 14px;
                }

                .rc-logo-img {
                    width: 125px;
                    height: auto;
                    object-fit: contain;
                    filter: drop-shadow(0 2px 4px rgba(22, 78, 158, 0.15));
                }

                .rc-logo-text {
                    font-size: 1.15rem;
                    font-weight: 900;
                    letter-spacing: 2px;
                    margin-top: 4px;
                    color: #164e9e;
                }

                .rc-meta-block {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .rc-top-titles {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    border-bottom: 1.5px solid #164e9e;
                    padding-bottom: 4px;
                    margin-bottom: 4px;
                }

                .rc-titulos {
                    display: flex;
                    flex-direction: column;
                }

                .rc-tit-principal {
                    font-size: 1.15rem;
                    font-weight: 900;
                    letter-spacing: 0.5px;
                    margin: 0;
                    line-height: 1.1;
                }

                .rc-tit-secundario {
                    font-size: 1rem;
                    font-weight: 800;
                    letter-spacing: 1px;
                    margin: 0;
                }

                .rc-num-recibo {
                    font-size: 1.45rem;
                    font-weight: 900;
                    color: #e11d48;
                    font-family: 'Courier New', Courier, monospace;
                    letter-spacing: 1px;
                }

                .rc-line-input {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.86rem;
                    font-weight: 800;
                }

                .rc-line-fill {
                    flex: 1;
                    border: none;
                    border-bottom: 1.5px solid #164e9e;
                    background: transparent;
                    color: #0f172a;
                    font-weight: 700;
                    font-size: 0.88rem;
                    padding: 2px 4px;
                    outline: none;
                }

                .rc-line-fill:read-only {
                    border-bottom-style: dotted;
                }

                .rc-armazon-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.86rem;
                    font-weight: 800;
                    padding: 8px 0;
                }

                /* Tabla de Refracción */
                .rc-tabla-refraccion {
                    width: 100%;
                    border-collapse: collapse;
                    border: 2px solid #164e9e;
                    margin-bottom: 10px;
                }

                .rc-tabla-refraccion th, .rc-tabla-refraccion td {
                    border: 1.5px solid #164e9e;
                    padding: 4px 6px;
                    text-align: center;
                    font-size: 0.8rem;
                }

                .rc-tabla-refraccion th {
                    background: #f0f7ff;
                    color: #164e9e;
                    font-weight: 800;
                    font-size: 0.74rem;
                    letter-spacing: 0.03em;
                }

                .rc-tabla-refraccion td input {
                    width: 100%;
                    border: none;
                    background: transparent;
                    text-align: center;
                    font-weight: 700;
                    font-size: 0.86rem;
                    color: #0f172a;
                    outline: none;
                }

                /* 3 Columnas con Casillas */
                .rc-grid-opciones {
                    display: grid;
                    grid-template-columns: 1fr 1.25fr 1.25fr;
                    gap: 12px;
                    border: 1.5px solid #164e9e;
                    border-radius: 4px;
                    padding: 10px 12px;
                    margin-bottom: 10px;
                    background: #fafcff;
                }

                .rc-col-titulo {
                    font-size: 0.8rem;
                    font-weight: 900;
                    text-align: center;
                    letter-spacing: 0.05em;
                    border-bottom: 1.5px solid #164e9e;
                    padding-bottom: 4px;
                    margin-bottom: 8px;
                }

                .rc-item-check {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    font-size: 0.76rem;
                    font-weight: 700;
                    margin-bottom: 4px;
                    cursor: pointer;
                    user-select: none;
                }

                .rc-box {
                    width: 15px;
                    height: 15px;
                    border: 1.5px solid #164e9e;
                    border-radius: 2px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff;
                    font-size: 0.75rem;
                    font-weight: 900;
                    color: #164e9e;
                    margin-left: 6px;
                }

                .rc-box.checked {
                    background: #164e9e;
                    color: #fff;
                }

                /* Observaciones */
                .rc-obs-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.84rem;
                    font-weight: 800;
                    margin-bottom: 8px;
                }

                /* Pagos y Firmas */
                .rc-pagos-firmas-grid {
                    display: grid;
                    grid-template-columns: 180px 1fr;
                    gap: 16px;
                    align-items: center;
                    margin: 8px 0;
                }

                .rc-totales-table {
                    width: 100%;
                    border-collapse: collapse;
                    border: 2px solid #164e9e;
                }

                .rc-totales-table td {
                    border: 1.5px solid #164e9e;
                    padding: 5px 8px;
                    font-size: 0.86rem;
                }

                .rc-totales-table td.lbl {
                    font-weight: 900;
                    background: #f0f7ff;
                    width: 70px;
                }

                .rc-totales-table td.val {
                    text-align: right;
                    font-weight: 800;
                    color: #0f172a;
                }

                .rc-firmas-block {
                    display: flex;
                    justify-content: space-around;
                    align-items: flex-end;
                    padding-top: 30px;
                }

                .rc-firma-linea {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 160px;
                }

                .rc-firma-bar {
                    width: 100%;
                    border-top: 1.5px solid #164e9e;
                    margin-bottom: 4px;
                }

                .rc-firma-lbl {
                    font-size: 0.74rem;
                    font-weight: 900;
                    letter-spacing: 0.04em;
                }

                /* Política de Devolución */
                .rc-politica-box {
                    border: 1.5px solid #164e9e;
                    border-radius: 4px;
                    padding: 8px 10px;
                    margin: 10px 0;
                    text-align: center;
                    font-size: 0.72rem;
                    line-height: 1.35;
                    background: #fafcff;
                }

                .rc-politica-tit {
                    font-weight: 900;
                    font-size: 0.78rem;
                    letter-spacing: 0.05em;
                    margin-bottom: 3px;
                }

                .rc-politica-nota {
                    font-weight: 900;
                    margin-top: 4px;
                }

                /* Pie con Contactos */
                .rc-footer-info {
                    border-top: 1.5px solid #164e9e;
                    padding-top: 6px;
                    font-size: 0.74rem;
                }

                .rc-footer-tit {
                    text-align: center;
                    font-weight: 900;
                    letter-spacing: 0.08em;
                    margin-bottom: 4px;
                }

                .rc-footer-cols {
                    display: grid;
                    grid-template-columns: 1.1fr 1.3fr 1fr;
                    gap: 8px;
                    align-items: center;
                    font-weight: 700;
                }

                /* Estilo de Impresión para Talonario */
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .recibo-talonario-container, .recibo-talonario-container * {
                        visibility: visible;
                    }
                    .recibo-talonario-container {
                        position: fixed;
                        left: 0;
                        top: 0;
                        width: 100% !important;
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 12px 16px !important;
                        border: 2px solid #164e9e !important;
                        box-shadow: none !important;
                        background: #fff !important;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                }
            `}</style>

            {/* ENCABEZADO */}
            <div className="rc-encabezado">
                <div className="rc-logo-block">
                    <img src={logoVision} alt="Vision Click" className="rc-logo-img" />
                    <div className="rc-logo-text">VISION CLICK</div>
                </div>

                <div className="rc-meta-block">
                    <div className="rc-top-titles">
                        <div className="rc-titulos">
                            <span className="rc-tit-principal">DETALLE DE COMPRA</span>
                            <span className="rc-tit-secundario">RECIBO</span>
                        </div>
                        <div className="rc-num-recibo">
                            {d.numero}
                        </div>
                    </div>

                    <div className="rc-line-input">
                        <span>NOMBRE :</span>
                        <input
                            className="rc-line-fill"
                            value={d.nombre}
                            readOnly={!editable}
                            onChange={e => onChange('cliente_nombre', e.target.value.toUpperCase())}
                            placeholder="Nombre y Apellidos del paciente"
                        />
                    </div>

                    <div className="rc-line-input">
                        <span>FECHA:</span>
                        <input
                            className="rc-line-fill"
                            type={editable ? "date" : "text"}
                            value={d.fecha}
                            readOnly={!editable}
                            onChange={e => onChange('fecha_emision', e.target.value)}
                        />
                    </div>

                    <div className="rc-line-input">
                        <span>TELÉFONO:</span>
                        <input
                            className="rc-line-fill"
                            value={d.telefono}
                            readOnly={!editable}
                            onChange={e => onChange('cliente_telefono', e.target.value)}
                            placeholder="Teléfono o Celular"
                        />
                    </div>
                </div>
            </div>

            {/* LÍNEA DE ARMAZÓN */}
            <div className="rc-armazon-row">
                <span>ARMAZÓN:</span>
                <input
                    className="rc-line-fill"
                    value={d.armazon}
                    readOnly={!editable}
                    onChange={e => onChange('armazon', e.target.value)}
                    placeholder="Marca, modelo, código o color del armazón"
                />
            </div>

            {/* TABLA DE REFRACCIÓN */}
            <table className="rc-tabla-refraccion">
                <thead>
                    <tr>
                        <th style={{ width: 44 }}>👁</th>
                        <th>ESFERA</th>
                        <th>CILINDRO</th>
                        <th>EJE</th>
                        <th>ADICIÓN</th>
                        <th>DISTANCIA PUPILAR</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ fontWeight: 900, background: '#f8fafc' }}>OD</td>
                        <td><input value={d.esfera_od} readOnly={!editable} onChange={e => onChange('esfera_od', e.target.value)} placeholder="0.00" /></td>
                        <td><input value={d.cilindro_od} readOnly={!editable} onChange={e => onChange('cilindro_od', e.target.value)} placeholder="0.00" /></td>
                        <td><input value={d.eje_od} readOnly={!editable} onChange={e => onChange('eje_od', e.target.value)} placeholder="0°" /></td>
                        <td><input value={d.adicion_od} readOnly={!editable} onChange={e => onChange('adicion_od', e.target.value)} placeholder="ADD" /></td>
                        <td><input value={d.dp_od} readOnly={!editable} onChange={e => onChange('dp_od', e.target.value)} placeholder="DP" /></td>
                    </tr>
                    <tr>
                        <td style={{ fontWeight: 900, background: '#f8fafc' }}>OI</td>
                        <td><input value={d.esfera_oi} readOnly={!editable} onChange={e => onChange('esfera_oi', e.target.value)} placeholder="0.00" /></td>
                        <td><input value={d.cilindro_oi} readOnly={!editable} onChange={e => onChange('cilindro_oi', e.target.value)} placeholder="0.00" /></td>
                        <td><input value={d.eje_oi} readOnly={!editable} onChange={e => onChange('eje_oi', e.target.value)} placeholder="0°" /></td>
                        <td><input value={d.adicion_oi} readOnly={!editable} onChange={e => onChange('adicion_oi', e.target.value)} placeholder="ADD" /></td>
                        <td><input value={d.dp_oi} readOnly={!editable} onChange={e => onChange('dp_oi', e.target.value)} placeholder="DP" /></td>
                    </tr>
                </tbody>
            </table>

            {/* 3 COLUMNAS CON CHECKBOXES */}
            <div className="rc-grid-opciones">
                {/* Columna 1: Tipo Lente */}
                <div>
                    <div className="rc-col-titulo">TIPO LENTE</div>
                    {TIPOS_LENTE.map(t => {
                        const checked = tiene('tipo_lente', t);
                        return (
                            <div key={t} className="rc-item-check" onClick={() => toggleLista('tipo_lente', t)}>
                                <span>{t}</span>
                                <span className={`rc-box ${checked ? 'checked' : ''}`}>{checked ? '✔' : ''}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Columna 2: Materiales */}
                <div>
                    <div className="rc-col-titulo">MATERIALES</div>
                    {MATERIALES.map(m => {
                        const checked = tiene('materiales', m);
                        return (
                            <div key={m} className="rc-item-check" onClick={() => toggleLista('materiales', m)}>
                                <span>{m}</span>
                                <span className={`rc-box ${checked ? 'checked' : ''}`}>{checked ? '✔' : ''}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Columna 3: Características */}
                <div>
                    <div className="rc-col-titulo">CARACTERÍSTICAS</div>
                    {CARACTERISTICAS.map(c => {
                        const checked = tiene('caracteristicas', c);
                        return (
                            <div key={c} className="rc-item-check" onClick={() => toggleLista('caracteristicas', c)}>
                                <span>{c}</span>
                                <span className={`rc-box ${checked ? 'checked' : ''}`}>{checked ? '✔' : ''}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* OBSERVACIONES */}
            <div className="rc-obs-row">
                <span>OBSERVACIONES:</span>
                <input
                    className="rc-line-fill"
                    value={d.observaciones}
                    readOnly={!editable}
                    onChange={e => onChange('observaciones', e.target.value)}
                    placeholder="Detalles de entrega, estuche, paño o indicaciones"
                />
            </div>

            {/* RECIBÍ DE / LA CANTIDAD DE */}
            <div className="rc-obs-row">
                <span>RECIBÍ DE:</span>
                <input
                    className="rc-line-fill"
                    value={d.recibi_de}
                    readOnly={!editable}
                    onChange={e => onChange('recibi_de', e.target.value.toUpperCase())}
                />
            </div>
            <div className="rc-obs-row">
                <span>LA CANTIDAD DE:</span>
                <input
                    className="rc-line-fill"
                    value={d.la_cantidad_de}
                    readOnly={!editable}
                    onChange={e => onChange('la_cantidad_de', e.target.value)}
                    placeholder="Valor en letras o detalle de pago"
                />
            </div>

            {/* PAGOS (TOTAL, ABONO, SALDO) Y FIRMAS */}
            <div className="rc-pagos-firmas-grid">
                <table className="rc-totales-table">
                    <tbody>
                        <tr>
                            <td className="lbl">TOTAL</td>
                            <td className="val">
                                {editable ? (
                                    <input
                                        type="number"
                                        step="0.01"
                                        style={{ width: '100%', textAlign: 'right', border: 'none', fontWeight: 800 }}
                                        value={d.total}
                                        onChange={e => {
                                            const tot = Number(e.target.value) || 0;
                                            onChange('total', tot);
                                            onChange('saldo', Math.max(0, tot - d.abono));
                                        }}
                                    />
                                ) : `$ ${d.total.toFixed(2)}`}
                            </td>
                        </tr>
                        <tr>
                            <td className="lbl">ABONO</td>
                            <td className="val">
                                {editable ? (
                                    <input
                                        type="number"
                                        step="0.01"
                                        style={{ width: '100%', textAlign: 'right', border: 'none', fontWeight: 800, color: '#059669' }}
                                        value={d.abono}
                                        onChange={e => {
                                            const ab = Number(e.target.value) || 0;
                                            onChange('abono', ab);
                                            onChange('saldo', Math.max(0, d.total - ab));
                                        }}
                                    />
                                ) : `$ ${d.abono.toFixed(2)}`}
                            </td>
                        </tr>
                        <tr>
                            <td className="lbl" style={{ color: '#dc2626' }}>SALDO</td>
                            <td className="val" style={{ color: '#dc2626', fontWeight: 900 }}>
                                {editable ? (
                                    <input
                                        type="number"
                                        step="0.01"
                                        style={{ width: '100%', textAlign: 'right', border: 'none', fontWeight: 900, color: '#dc2626' }}
                                        value={d.saldo}
                                        onChange={e => onChange('saldo', Number(e.target.value) || 0)}
                                    />
                                ) : `$ ${d.saldo.toFixed(2)}`}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div className="rc-firmas-block">
                    <div className="rc-firma-linea">
                        <div className="rc-firma-bar"></div>
                        <span className="rc-firma-lbl">RECIBÍ CONFORME</span>
                    </div>
                    <div className="rc-firma-linea">
                        <div className="rc-firma-bar"></div>
                        <span className="rc-firma-lbl">ENTREGUÉ CONFORME</span>
                    </div>
                </div>
            </div>

            {/* POLÍTICA DE NO DEVOLUCIÓN */}
            <div className="rc-politica-box">
                <div className="rc-politica-tit">POLÍTICA DE NO DEVOLUCIÓN</div>
                <div>
                    Todas las ventas son definitivas, no realizamos devoluciones o reembolsos. Apreciamos tu comprensión y te aseguramos que estamos comprometidos a entregar productos y servicios de alta calidad, estamos prestos a brindar solución a cualquier problema que pueda surgir.
                </div>
                <div className="rc-politica-nota">
                    Nota: Pasado los 30 días pierde derecho a su abono y a sus lentes.
                </div>
            </div>

            {/* INFORMACIÓN DE CONTACTO / FOOTER */}
            <div className="rc-footer-info">
                <div className="rc-footer-tit">INFORMACIÓN</div>
                <div className="rc-footer-cols">
                    <div>
                        <div><i className="bi bi-whatsapp me-1"></i>0993 387 008</div>
                        <div><i className="bi bi-telephone me-1"></i>0998 940 200</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div>Veintimilla y Camilo Ponce</div>
                        <div>Rafael Arellano y Ayacucho</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div><i className="bi bi-instagram me-1"></i>@visio.nclick</div>
                        <div><i className="bi bi-facebook me-1"></i>Visión Click</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReciboVentaDocumento;
